import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pageTitle } from '../../helper';
import PageHeading from '../PageHeading';
import Div from '../Div';
import Spacing from '../Spacing';
import { Helmet } from 'react-helmet';
import { bookingsAPI } from '../../helper/bookingsAPI';
import './CalendarPage.css';

export default function CalendarPage() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  pageTitle(t('calendar.pageTitle'));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    window.scrollTo(0, 0);
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const data = await bookingsAPI.getBookings();
      setBookings(data.bookings || []);
    } catch (error) {
      console.error('Chyba při načítání rezervací:', error);
      setBookings([]);
      try {
        const response = await fetch('/bookings.json');
        const fallbackData = await response.json();
        setBookings(fallbackData.bookings || []);
      } catch (fallbackError) {
        console.error('Fallback také selhal:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const getMonthDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = (firstDay.getDay() + 6) % 7;
    const dates = [];
    // Day 0 of current month is the last day of previous month.
    // Using month - 1 here can overflow filler dates (e.g. create duplicated day 1).
    const prevMonth = new Date(year, month, 0);
    const prevMonthLastDay = prevMonth.getDate();

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(year, month - 1, day);
      dates.push({ date, isCurrentMonth: false });
    }

    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      dates.push({ date, isCurrentMonth: true });
    }

    const remainingDays = 42 - dates.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      dates.push({ date, isCurrentMonth: false });
    }

    return dates;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatMonthYear = (date) => {
    const locale = isEn ? 'en-US' : 'cs-CZ';
    return date.toLocaleDateString(locale, {
      month: 'long',
      year: 'numeric'
    });
  };

  const getBookingForDate = (date) => {
    const dateStr = formatDate(date);
    return bookings.find(booking => booking.date === dateStr);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const dayNames = t('calendar.dayNames', { returnObjects: true });
  const monthDates = getMonthDates(currentMonth);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{t('calendar.metaTitle')}</title>
          <link rel="icon" href="/stablerecords.ico" />
          <meta name="description" content={t('calendar.metaDescription')} />
        </Helmet>
        <PageHeading
          title={t('calendar.pageTitle')}
          bgSrc='images/service_hero_bg.jpeg'
          pageLinkText={t('calendar.pageLinkText')}
        />
        <Spacing lg='150' md='80'/>
        <Div className="container">
          <div className="text-center">{t('calendar.loading')}</div>
        </Div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('calendar.metaTitle')}</title>
        <link rel="icon" href="/stablerecords.ico" />
        <meta name="description" content={t('calendar.metaDescription')} />
      </Helmet>

      <PageHeading
        title={t('calendar.pageTitle')}
        bgSrc='images/service_hero_bg.jpeg'
        pageLinkText={t('calendar.pageLinkText')}
      />

      <Div className="container">
        <Div className="row">
          <Div className="col-12">
            <div className="calendar-navigation">
              <button
                className="btn btn-outline-primary"
                onClick={() => navigateMonth(-1)}
              >
                ← {t('calendar.prevMonth')}
              </button>
              <span className="current-month">
                {formatMonthYear(currentMonth)}
              </span>
              <button
                className="btn btn-outline-primary"
                onClick={() => navigateMonth(1)}
              >
                {t('calendar.nextMonth')} →
              </button>
            </div>

            <Spacing lg='30' md='20'/>

            <div className="calendar-container">
              <div className="calendar-grid">
                <div className="calendar-header">
                  {dayNames.map((dayName) => (
                    <div key={dayName} className="day-header">
                      {dayName}
                    </div>
                  ))}
                </div>

                {monthDates.map((dateObj, index) => {
                  const booking = getBookingForDate(dateObj.date);
                  const isBooked = !!booking;

                  return (
                    <div
                      key={index}
                      className={`calendar-cell ${isBooked ? 'occupied' : 'free'} ${!dateObj.isCurrentMonth ? 'other-month' : ''}`}
                      title={booking ? booking.description : t('calendar.free')}
                    >
                      <div className="day-number">
                        {dateObj.date.getDate()}
                      </div>
                      {isBooked && booking && (
                        <div className="booking-info">
                          <div className="booking-description">
                            {booking.description}
                          </div>
                        </div>
                      )}
                      {!isBooked && dateObj.isCurrentMonth && (
                        <span className="free-text">{t('calendar.free')}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Spacing lg='50' md='30'/>

            <div className="calendar-legend">
              <div className="legend-item">
                <div className="legend-color occupied"></div>
                <span>{t('calendar.occupied')}</span>
              </div>
              <div className="legend-item">
                <div className="legend-color free"></div>
                <span>{t('calendar.free')}</span>
              </div>
            </div>

          </Div>
        </Div>
      </Div>

      <Spacing lg='150' md='80'/>
    </>
  );
}